import type { Directive } from 'vue'

interface DraggableElement extends HTMLElement {
  _dragData?: {
    isDragging: boolean
    initialX: number
    initialY: number
    currentX: number
    currentY: number
  }
  _cleanupDrag?: () => void
}

/**
 * Vue directive to make an element draggable within a v-dialog
 *
 * Note: This directive only activates when the element is inside a v-dialog (v-overlay).
 * Cards used directly on the page will not be draggable, and drag handle visual indicators
 * (cursor styles and tooltips) will be automatically removed.
 *
 * Usage:
 * ```vue
 * <v-dialog v-model="dialog">
 *   <v-card v-draggable class="cursor-grab">
 *     <v-card-title>Draggable Dialog</v-card-title>
 *   </v-card>
 * </v-dialog>
 * ```
 *
 * The directive makes the entire element draggable by clicking and dragging anywhere on it.
 * You can limit dragging to specific areas by using the `data-drag-handle` attribute:
 *
 * ```vue
 * <v-dialog v-model="dialog">
 *   <v-card v-draggable>
 *     <v-card-title data-drag-handle class="cursor-grab">
 *       Drag from here only
 *     </v-card-title>
 *     <v-card-text>Content here (not draggable)</v-card-text>
 *   </v-card>
 * </v-dialog>
 * ```
 */
export const draggable: Directive = {
  mounted(el: DraggableElement) {
    // Only enable dragging if the element is inside a v-dialog (v-overlay)
    const isInDialog = el.closest('.v-overlay__content') !== null
    if (!isInDialog) {
      // Not in a dialog - remove drag handle visual indicators
      const handle: HTMLElement | null = el.querySelector('[data-drag-handle]')
      if (handle) {
        handle.classList.remove('cursor-grab')
        handle.removeAttribute('title')
        handle.style.cursor = ''
        handle.style.userSelect = ''
      }
      return // Skip draggable functionality
    }

    // Check if there's a specific drag handle, otherwise use the entire element
    const handle: HTMLElement = el.querySelector('[data-drag-handle]') || el

    if (!handle) return

    // Initialize drag data
    el._dragData = {
      isDragging: false,
      initialX: 0,
      initialY: 0,
      currentX: 0,
      currentY: 0
    }

    const startDrag = (e: MouseEvent | TouchEvent) => {
      if (!el._dragData) return

      // Prevent duplicate listener registration if already dragging
      if (el._dragData.isDragging) return

      // Don't drag if clicking on interactive elements
      const target = e.target as HTMLElement
      if (
        target.tagName === 'BUTTON' ||
        target.tagName === 'INPUT' ||
        target.tagName === 'SELECT' ||
        target.tagName === 'TEXTAREA' ||
        target.closest('button') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea')
      ) {
        return
      }

      // Check if click is within the handle
      if (!handle.contains(target)) return

      el._dragData.isDragging = true

      if (e.type === 'touchstart') {
        const touch = (e as TouchEvent).touches[0]
        if (!touch) return
        el._dragData.initialX = touch.clientX - el._dragData.currentX
        el._dragData.initialY = touch.clientY - el._dragData.currentY
      } else {
        const mouseEvent = e as MouseEvent
        el._dragData.initialX = mouseEvent.clientX - el._dragData.currentX
        el._dragData.initialY = mouseEvent.clientY - el._dragData.currentY
      }

      // Add visual feedback
      if (handle.style) {
        handle.style.cursor = 'grabbing'
      }

      document.addEventListener('mousemove', drag)
      document.addEventListener('touchmove', drag, { passive: false })
      document.addEventListener('mouseup', stopDrag)
      document.addEventListener('touchend', stopDrag)
    }

    const drag = (e: MouseEvent | TouchEvent) => {
      if (!el._dragData || !el._dragData.isDragging) return

      e.preventDefault()

      if (e.type === 'touchmove') {
        const touch = (e as TouchEvent).touches[0]
        if (!touch) return
        el._dragData.currentX = touch.clientX - el._dragData.initialX
        el._dragData.currentY = touch.clientY - el._dragData.initialY
      } else {
        const mouseEvent = e as MouseEvent
        el._dragData.currentX = mouseEvent.clientX - el._dragData.initialX
        el._dragData.currentY = mouseEvent.clientY - el._dragData.initialY
      }

      el.style.transform = `translate(${el._dragData.currentX}px, ${el._dragData.currentY}px)`
    }

    const stopDrag = () => {
      if (!el._dragData) return

      el._dragData.isDragging = false

      // Restore cursor
      if (handle.style) {
        handle.style.cursor = 'grab'
      }

      document.removeEventListener('mousemove', drag)
      document.removeEventListener('touchmove', drag, { passive: false } as EventListenerOptions)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchend', stopDrag)
    }

    // Set initial cursor style
    if (handle.style) {
      handle.style.cursor = 'grab'
      handle.style.userSelect = 'none'
    }

    // Attach the mousedown and touchstart events to the handle
    handle.addEventListener('mousedown', startDrag)
    handle.addEventListener('touchstart', startDrag, { passive: true })

    // Store cleanup function
    el._cleanupDrag = () => {
      handle.removeEventListener('mousedown', startDrag)
      handle.removeEventListener('touchstart', startDrag, { passive: true } as EventListenerOptions)
      document.removeEventListener('mousemove', drag)
      document.removeEventListener('touchmove', drag, { passive: false } as EventListenerOptions)
      document.removeEventListener('mouseup', stopDrag)
      document.removeEventListener('touchend', stopDrag)
      if (handle.style) {
        handle.style.cursor = ''
        handle.style.userSelect = ''
      }
    }
  },

  unmounted(el: DraggableElement) {
    // Cleanup event listeners
    if (el._cleanupDrag) {
      el._cleanupDrag()
    }
    delete el._dragData
    delete el._cleanupDrag
  }
}
